import { DatePipe, UpperCasePipe } from '@angular/common'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	OutputEmitterRef,
	Signal,
	signal,
	SimpleChanges,
	WritableSignal,
	input,
	output,
	inject,
} from '@angular/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon } from '@taiga-ui/core'
import { finalize, Subject, takeUntil } from 'rxjs'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { TuiSkeleton } from '@taiga-ui/kit'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { HttpErrorResponse } from '@angular/common/http'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import { ProfitTriangleComponent } from '@shared/components/trading/common/profit-triangle/profit-triangle.component'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { AlertService } from '@shared/services/alert.service'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { LiveTickerPricesInterfaces } from '@modules/stocks-module/live-ticker/interfaces/live-ticker-prices.interfaces'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { SecondaryLiveTickerService } from '@modules/stocks-module/live-ticker/services/secondary-live-ticker.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-position-option-info',
	templateUrl: './position-option-info.component.html',
	styleUrl: './position-option-info.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TranslatePipe,
		HighlightDirective,
		UpperCasePipe,
		TuiSkeleton,
		DatePipe,
		CompanyLogoComponent,
		ProfitTriangleComponent,
		PricePipe,
		QuantityPipe,
		ProfitPipe,
		PercentPipe,
	],
})
export class PositionOptionInfoComponent implements OnChanges, OnDestroy, OnInit {
	protected readonly positionSideEnum: typeof PositionSideEnum = PositionSideEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum

	public position: InputSignal<PositionOptionInterface | null> = input<PositionOptionInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _secondaryLiveTickerService: SecondaryLiveTickerService =
		inject<SecondaryLiveTickerService>(SecondaryLiveTickerService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)

	protected assetsInfo: WritableSignal<AssetExanteInterface | null> = signal<AssetExanteInterface | null>(null)
	protected liveTicker: WritableSignal<LiveTickerInterface | null> = signal<LiveTickerInterface | null>(null)
	protected isLoadingAssetInfo: WritableSignal<boolean> = signal<boolean>(false)

	protected tickerPrices: Signal<Partial<LiveTickerPricesInterfaces> | null> =
		computed<Partial<LiveTickerPricesInterfaces> | null>((): Partial<LiveTickerPricesInterfaces> | null => {
			const liveTicker: LiveTickerInterface | null = this.liveTicker()
			if (!liveTicker) return null

			const isMarketClose: boolean =
				this._marketStatusHelperService.marketStatus === this.marketStatusEnum.marketClose
			const primary: Partial<LiveTickerPricesInterfaces> | null = isMarketClose
				? liveTicker.currentPrices
				: liveTicker.marketStatusPrices
			const fallback: Partial<LiveTickerPricesInterfaces> | null = isMarketClose
				? liveTicker.marketStatusPrices
				: liveTicker.currentPrices

			return {
				price: primary?.price || fallback?.price,
				changeMoney: primary?.changeMoney || fallback?.changeMoney,
				changePercent: primary?.changePercent || fallback?.changePercent,
			}
		})
	private _listenLiveTicker(): void {
		this._secondaryLiveTickerService.liveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((liveTicker: LiveTickerInterface | null): void => {
				this.liveTicker.set(liveTicker)
			})
	}

	private _setLiveTicker(symbol: string): void {
		this._secondaryLiveTickerService.setLiveTicker(symbol)
	}

	private _getAssetsInfo(symbol: string): void {
		this.isLoadingAssetInfo.set(true)

		this._stocksService
			.getExanteAsset(symbol)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingAssetInfo.set(false)),
			)
			.subscribe(
				(assets: AssetExanteInterface): void => {
					this.assetsInfo.set(assets)
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message || this._translateService.instant('positionInfo.assetsInfoFailed'),
					)
				},
			)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['position']) {
			const prevSymbol: string | undefined = changes['position'].previousValue?.option_ticker
			const currentSymbol: string | undefined = changes['position'].currentValue?.option_ticker

			if (currentSymbol && currentSymbol !== prevSymbol) {
				this._getAssetsInfo(currentSymbol)
				this._setLiveTicker(currentSymbol)
			}
		}
	}

	ngOnInit(): void {
		this._listenLiveTicker()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
